import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Mail, MessageCircle, FileText, ChevronDown, ChevronUp,
  HelpCircle, CreditCard, User, Shield, Smartphone, AlertCircle
} from 'lucide-react';
import { useSchema } from '../hooks/useSchema';

const PublicSupport = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqCategories = [
    {
      title: "Getting Started",
      icon: User,
      faqs: [
        {
          question: "How do I create an account?",
          answer: "Visit our homepage and click 'Get Started'. You can sign up with your email address. Once registered, you'll have access to our free tier tools immediately."
        },
        {
          question: "Is CoRegulate free to use?",
          answer: "Yes! We offer a free tier with access to core regulation tools. For advanced features like AI companion, hypnotherapy sessions, and relationship tools, you can upgrade to our premium plans."
        },
        {
          question: "Do I need an ADHD diagnosis to use CoRegulate?",
          answer: "No diagnosis is required. Our tools are designed for anyone who experiences emotional dysregulation, though they're specifically optimized for ADHD minds."
        }
      ]
    },
    {
      title: "Subscription & Billing",
      icon: CreditCard,
      faqs: [
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards, debit cards, and Apple Pay through our secure payment processor, Stripe."
        },
        {
          question: "How do I cancel my subscription?",
          answer: "You can cancel anytime from your Account settings. Your access will continue until the end of your current billing period. No questions asked."
        },
        {
          question: "Do you offer refunds?",
          answer: "Yes, we offer refunds on a case-by-case basis within 14 days of purchase. Contact us at support@coregulateadhd.com to request a refund."
        }
      ]
    },
    {
      title: "Privacy & Security",
      icon: Shield,
      faqs: [
        {
          question: "Is my data private?",
          answer: "Absolutely. We take privacy seriously. Your emotional check-ins and personal data are encrypted and never sold to third parties. See our Privacy Policy for full details."
        },
        {
          question: "Can I delete my data?",
          answer: "Yes. You can delete your account and all associated data at any time from your Account settings. This action is permanent and immediate."
        },
        {
          question: "Are my AI conversations stored?",
          answer: "Conversations with our AI companion are stored to provide context within your session and improve your experience. They are encrypted and only accessible by you."
        }
      ]
    },
    {
      title: "Using the App",
      icon: Smartphone,
      faqs: [
        {
          question: "Which devices can I use CoRegulate on?",
          answer: "CoRegulate works on any device with a modern web browser — phones, tablets, and computers. We're optimized for mobile use when you need quick regulation on the go."
        },
        {
          question: "Do I need an internet connection?",
          answer: "Currently, yes. An internet connection is required to access our tools and AI features. We're exploring offline capabilities for future updates."
        },
        {
          question: "How do I contact the AI companion?",
          answer: "Once logged in, you'll see a chat icon in the corner of your screen. Click it anytime to start a conversation with your AI companion."
        }
      ]
    }
  ];

  const toggleFaq = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenFaq(openFaq === key ? null : key);
  };

  // SEO Schema - FAQPage
  useSchema({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How do I create an account?", "acceptedAnswer": { "@type": "Answer", "text": "Visit our homepage and click 'Get Started'. You can sign up with your email address. Once registered, you'll have access to our free tier tools immediately." }},
      { "@type": "Question", "name": "Is CoRegulate free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! We offer a free tier with access to core regulation tools. For advanced features like AI companion, hypnotherapy sessions, and relationship tools, you can upgrade to our premium plans." }},
      { "@type": "Question", "name": "Do I need an ADHD diagnosis to use CoRegulate?", "acceptedAnswer": { "@type": "Answer", "text": "No diagnosis is required. Our tools are designed for anyone who experiences emotional dysregulation, though they're specifically optimized for ADHD minds." }},
      { "@type": "Question", "name": "What payment methods do you accept?", "acceptedAnswer": { "@type": "Answer", "text": "We accept all major credit cards, debit cards, and Apple Pay through our secure payment processor, Stripe." }},
      { "@type": "Question", "name": "How do I cancel my subscription?", "acceptedAnswer": { "@type": "Answer", "text": "You can cancel anytime from your Account settings. Your access will continue until the end of your current billing period." }},
      { "@type": "Question", "name": "Is my data private?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. We take privacy seriously. Your emotional check-ins and personal data are encrypted and never sold to third parties." }}
    ]
  });

  return (
    <>
      <Helmet>
        <title>Support & Help Center | CoRegulate - ADHD Emotional Regulation App</title>
        <meta name="description" content="Get help with CoRegulate. Find answers to common questions, contact support, and learn how to make the most of your ADHD emotional regulation tools." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://coregulateadhd.com/help" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Support & Help Center | CoRegulate" />
        <meta property="og:description" content="Get help with CoRegulate. Find answers to common questions and contact our support team." />
        <meta property="og:url" content="https://coregulateadhd.com/help" />
        <meta property="og:site_name" content="CoRegulate" />
        <meta property="og:image" content="https://coregulateadhd.com/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Support & Help Center | CoRegulate" />
        <meta name="twitter:description" content="Get help with CoRegulate. Find answers to common questions and contact our support team." />
        <meta name="twitter:image" content="https://coregulateadhd.com/og-image.png" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-card border-b border-border">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Link>
            <Link 
              to="/auth" 
              className="bg-primary text-primary-foreground px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium hover:bg-primary/90 transition-colors flex-shrink-0 ml-2"
            >
              Sign In
            </Link>
          </div>
        </header>

        <main>
          {/* Hero Section */}
          <section className="py-16 md:py-20 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <HelpCircle className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-4xl md:text-5xl font-serif text-foreground mb-6">
                How Can We Help?
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Find answers to common questions or reach out to our support team. 
                We're here to help you get the most out of CoRegulate.
              </p>
            </div>
          </section>

          {/* Contact Options */}
          <section className="pb-12 px-4">
            <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-4">
              <a 
                href="mailto:support@coregulateadhd.com"
                className="bg-card rounded-xl p-6 border border-border hover:border-primary/50 transition-colors text-center"
              >
                <Mail className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-1">Email Support</h3>
                <p className="text-sm text-muted-foreground">support@coregulateadhd.com</p>
                <p className="text-xs text-muted-foreground mt-2">Response within 24 hours</p>
              </a>
              <Link 
                to="/faq"
                className="bg-card rounded-xl p-6 border border-border hover:border-primary/50 transition-colors text-center"
              >
                <FileText className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-1">Full FAQ</h3>
                <p className="text-sm text-muted-foreground">Browse all questions</p>
                <p className="text-xs text-muted-foreground mt-2">Detailed answers</p>
              </Link>
              <Link 
                to="/contact"
                className="bg-card rounded-xl p-6 border border-border hover:border-primary/50 transition-colors text-center"
              >
                <MessageCircle className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-1">Contact Form</h3>
                <p className="text-sm text-muted-foreground">Send us a message</p>
                <p className="text-xs text-muted-foreground mt-2">For detailed inquiries</p>
              </Link>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-12 px-4 bg-card">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-serif text-foreground text-center mb-8">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-6">
                {faqCategories.map((category, categoryIndex) => (
                  <div key={category.title} className="bg-background rounded-xl border border-border overflow-hidden">
                    {/* Category Header */}
                    <div className="p-4 border-b border-border flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <category.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground">{category.title}</h3>
                    </div>
                    
                    {/* FAQs */}
                    <div className="divide-y divide-border">
                      {category.faqs.map((faq, faqIndex) => {
                        const isOpen = openFaq === `${categoryIndex}-${faqIndex}`;
                        return (
                          <div key={faqIndex}>
                            <button
                              onClick={() => toggleFaq(categoryIndex, faqIndex)}
                              className="w-full p-4 text-left flex items-center justify-between hover:bg-accent/50 transition-colors"
                            >
                              <span className="font-medium text-foreground pr-4">{faq.question}</span>
                              {isOpen ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                              )}
                            </button>
                            {isOpen && (
                              <div className="px-4 pb-4">
                                <p className="text-muted-foreground">{faq.answer}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Crisis Notice */}
          <section className="py-12 px-4">
            <div className="max-w-2xl mx-auto">
              <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-6 border border-amber-200 dark:border-amber-800 flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Need Immediate Help?</h3>
                  <p className="text-amber-700 dark:text-amber-300 text-sm mb-3">
                    If you're experiencing a mental health crisis, please reach out to professional support:
                  </p>
                  <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                    <li>• <strong>USA:</strong> 988 Suicide & Crisis Lifeline</li>
                    <li>• <strong>UK:</strong> 116 123 (Samaritans)</li>
                    <li>• <strong>Australia:</strong> 13 11 14 (Lifeline)</li>
                  </ul>
                  <Link 
                    to="/crisis" 
                    className="inline-block mt-3 text-sm text-amber-800 dark:text-amber-200 underline hover:no-underline"
                  >
                    View all crisis resources →
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Still Need Help */}
          <section className="py-12 px-4 bg-card">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-xl font-serif text-foreground mb-4">
                Still Need Help?
              </h2>
              <p className="text-muted-foreground mb-6">
                Can't find what you're looking for? Our team is happy to help with any questions.
              </p>
              <a 
                href="mailto:support@coregulateadhd.com"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Contact Support
              </a>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border">
          <div className="max-w-6xl mx-auto px-4 py-8 text-center space-x-6">
            <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">
              About
            </Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
              Terms
            </Link>
            <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
              Contact
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PublicSupport;
